<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { isAdsUser } from '@/services/userRoles'

const props = defineProps({
  images: {
    type: Array,
    required: true
  }
})

const emit = defineEmits(['delete-image'])

const currentIndex = ref(0)
const isTransitioning = ref(false)

// Igual que el botón de publicar del Inicio: cualquier perfil interno de ADS
// gestiona las noticias, no solo el responsable (ADS-0133).
const canManageNews = isAdsUser();

const previousIndex = computed(() =>
  currentIndex.value === 0 ? props.images.length - 1 : currentIndex.value - 1
)

const nextIndex = computed(() =>
  currentIndex.value === props.images.length - 1 ? 0 : currentIndex.value + 1
)

const handleDelete = (index) => {
  if (!canManageNews) return;
  emit('delete-image', index)
}

// Loop automático solo si hay 2 o más imágenes
let interval = null
onMounted(() => {
  if (props.images.length > 1) {
    interval = setInterval(() => {
      if (isTransitioning.value) return
      isTransitioning.value = true
      setTimeout(() => {
        currentIndex.value = nextIndex.value
        isTransitioning.value = false
      }, 500)
    }, 4000)
  }
})

onUnmounted(() => {
  if (interval) clearInterval(interval)
})
</script>

<template>
  <!-- Si no hay imágenes, no se muestra nada -->
  <div v-if="images && images.length > 0" class="flex items-center justify-center w-full pt-4">
    <div class="relative flex items-center justify-center w-full">
      <div class="relative flex items-center justify-center gap-2 w-full mx-auto">
        <!-- Si solo hay una imagen, mostrar solo esa imagen sin carrusel -->
        <template v-if="images.length === 1">
          <div class="transition-all duration-500 ease-in-out w-full shadow-lg z-20">
            <div class="relative w-full">
              <img :src="images[0]" alt="Imagen única" class="block w-full h-auto bg-white rounded-lg">
              <button v-if="canManageNews"
                @click.stop="handleDelete(0)"
                class="absolute top-2 right-2 w-2 h-2 p-4 flex items-center justify-center cursor-pointer bg-red-400 text-white rounded-full hover:bg-red-500 transition-colors leading-none duration-700 hover:scale-110 active:scale-100">
                X
              </button>
            </div>
          </div>
        </template>
        <!-- Si hay dos o más imágenes, mostrar el carrusel -->
        <template v-else>
          <!-- Imagen Anterior (Solo en pantallas medianas y grandes, solo si hay 3 o más) -->
          <div v-if="images.length > 2" class="hidden sm:block transition-all duration-500 ease-in-out opacity-50 brightness-75 w-[60%] h-[70%] absolute -left-[25%] z-10"
            :class="{ '-translate-x-2 opacity-40': isTransitioning }">
            <div class="relative w-full h-full">
              <img :src="images[previousIndex]" alt="Previous image"
                class="w-full h-full object-cover rounded-lg">
              <button v-if="canManageNews"
                @click.stop="handleDelete(previousIndex)"
                class="absolute top-2 left-2 w-2 h-2 p-4 flex items-center justify-center cursor-pointer bg-red-400 text-white rounded-full hover:bg-red-500 transition-colors leading-none duration-700 hover:scale-110 active:scale-100">
                X
              </button>
            </div>
          </div>

          <!-- Imagen Actual -->
          <div class="transition-all duration-500 ease-in-out w-full shadow-lg z-20"
            :class="{ '-translate-x-2 opacity-40': isTransitioning }">
            <div class="relative w-full">
              <img :src="images[currentIndex]" alt="Current image"
                class="block w-full h-auto bg-white rounded-lg">
              <button v-if="canManageNews"
                @click.stop="handleDelete(currentIndex)"
                class="absolute top-2 right-2 w-2 h-2 p-4 flex items-center justify-center cursor-pointer bg-red-400 text-white rounded-full hover:bg-red-500 transition-colors leading-none duration-700 hover:scale-110 active:scale-100">
                X
              </button>
            </div>
          </div>

          <!-- Imagen Siguiente (Solo en pantallas medianas y grandes, solo si hay 3 o más) -->
          <div v-if="images.length > 2" class="hidden sm:block transition-all duration-500 ease-in-out opacity-50 brightness-75 w-[60%] h-[70%] absolute -right-[25%] z-10"
            :class="{ 'translate-x-2 opacity-40': isTransitioning }">
            <div class="relative w-full h-full">
              <img :src="images[nextIndex]" alt="Next image"
                class="w-full h-full object-cover rounded-lg">
              <button v-if="canManageNews"
                @click.stop="handleDelete(nextIndex)"
                class="absolute top-2 right-2 w-2 h-2 p-4 flex items-center justify-center cursor-pointer bg-red-400 text-white rounded-full hover:bg-red-500 transition-colors leading-none duration-700 hover:scale-110 active:scale-100">
                X
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
